/**
 * Where an invitation link lands: who invited you, then create your account
 * (Syntera password rules, retyped, human check) or sign in to an existing
 * one. Accepting happens after sign-in and two-step setup, in App.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';

import { ApiError } from '../api.ts';
import { HumanCheck } from '../login/HumanCheck.tsx';
import { LoginShell, PASSWORD_RULES, PasswordChecklist } from '../login/LoginShell.tsx';
import { previewInvitation, register, type InvitationPreview } from './invite-api.ts';

export function InviteLanding({
  token,
  onSignIn,
}: {
  token: string;
  onSignIn: (email: string, notice?: string) => void;
}): ReactNode {
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [human, setHuman] = useState('');
  const [humanReset, setHumanReset] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    previewInvitation(token)
      .then(setPreview)
      .catch(() =>
        setProblem(
          'This invitation is invalid or has expired. Ask whoever invited you to send a new one.',
        ),
      );
  }, [token]);

  const ready =
    PASSWORD_RULES.every((rule) => rule.test(password)) &&
    password === confirm &&
    first.trim().length > 0;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!preview || !ready) return;
    setBusy(true);
    setProblem(null);
    try {
      await register({
        email: preview.email,
        password,
        first_name: first.trim(),
        last_name: last.trim(),
        turnstile_token: human,
      });
      onSignIn(preview.email, 'Account created. Sign in to finish joining.');
    } catch (cause) {
      setProblem(
        cause instanceof ApiError ? cause.message : 'The account could not be created. Try again.',
      );
      setHumanReset((count) => count + 1);
    } finally {
      setBusy(false);
    }
  };

  return (
    <LoginShell>
      <header className="li-head">
        <h1>You're invited</h1>
        <p>
          {preview
            ? `${preview.organisation_name} has invited ${preview.email} to see their reports.`
            : 'Checking your invitation…'}
        </p>
      </header>
      {problem && (
        <div className="li-error" role="alert">
          {problem}
        </div>
      )}
      {preview && (
        <form onSubmit={(event) => void submit(event)} className="li-form">
          <label htmlFor="invite-first">First name</label>
          <input
            id="invite-first"
            value={first}
            onChange={(event) => setFirst(event.target.value)}
            autoComplete="given-name"
            required
            autoFocus
          />
          <label htmlFor="invite-last">Last name</label>
          <input
            id="invite-last"
            value={last}
            onChange={(event) => setLast(event.target.value)}
            autoComplete="family-name"
          />
          <label htmlFor="invite-email">Email address</label>
          <input id="invite-email" type="email" value={preview.email} readOnly />
          <label htmlFor="invite-password">Choose a password</label>
          <input
            id="invite-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            required
          />
          <label htmlFor="invite-confirm">Retype password</label>
          <input
            id="invite-confirm"
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
          />
          <PasswordChecklist value={password} confirm={confirm} />
          <HumanCheck onToken={setHuman} resetKey={humanReset} />
          <button className="li-submit" type="submit" disabled={busy || !ready || !human}>
            {busy ? 'Creating your account…' : 'Create account →'}
          </button>
          <p className="li-after">
            Already have an account?{' '}
            <button type="button" className="li-link" onClick={() => onSignIn(preview.email)}>
              Sign in instead
            </button>
          </p>
        </form>
      )}
    </LoginShell>
  );
}
